// The idea of this helper is to have common methods that define how to update single vehicle offer on our store
// it can be used from list, vdp or some other places so we keep consistency between different slices

import { BID_SALE_AUCTION_STATUS } from 'constants/bidSale';
import dayjs from 'dayjs';
import { decodeVehicle } from 'helpers/timedSales';
import { isOlderVersion } from 'helpers/timedSales/versionChecker';
import { isCurrentUserTheTopOfferer } from 'helpers/marketplace/myOfferStatus';

const updateOfferStatusNewStructure = ({ currentOfferState, updatedOfferState }) => {
	if (
		!updatedOfferState ||
		isOlderVersion({ incomingChanges: updatedOfferState.offerData, storedState: currentOfferState?.offerData })
	) {
		return;
	}

	// if any update comes after set it as processing we assume that it move it to next state.
	return { ...updatedOfferState, isOvertimeCompletedButServerDidNotResolveTheSell: false };
};

export const updateOfferStatus = ({
	currentOfferState,
	updatedVehicleId,
	updatedOfferState: encodedChanges,
	loggedUser,
}) => {
	const isNewStructure = Boolean(encodedChanges?.offerData?.public);
	if (isNewStructure) return updateOfferStatusNewStructure({ currentOfferState, updatedOfferState: encodedChanges });

	// TODO: once backend_driven_offer_payload_react is on, delete all this code
	const changes = decodeVehicle(encodedChanges);
	const { best_offer_offerer_dealership_id } = changes;

	if (!currentOfferState || isOlderVersion({ incomingChanges: changes, storedState: currentOfferState })) return;

	if (updatedVehicleId && changes.id !== updatedVehicleId) delete changes.id; // this doesn't seems to make sense

	const isUserTopBidder = isCurrentUserTheTopOfferer({
		currentDealershipOrAnySubordinateIdThatOffered:
			currentOfferState.offerer_dealership_id || loggedUser?.dealership_id,
		best_offer_offerer_dealership_id,
		loggedUser,
	});

	if (isUserTopBidder) {
		if (changes.bid_sale_auction_status !== BID_SALE_AUCTION_STATUS.pending && changes.best_offer_amount) {
			changes.my_offer_amount = changes.best_offer_amount;
		}
		if (changes.best_offer_max_bid_amount) {
			changes.my_offer_max_bid_amount = changes.best_offer_max_bid_amount;
		}

		changes.my_offer_id = changes.best_offer_id;
		// last_offer_user is not sent on WS, so we need to set it manually if the current user is the top offerer
		if (
			changes.best_offer_offerer_id &&
			Number(currentOfferState?.last_offer_user?.id) !== Number(changes.best_offer_offerer_id)
		) {
			changes.last_offer_user = {
				id: changes.best_offer_offerer_id,
				name: changes.best_offer_user_name,
			};
		}
	}

	const notOvertimeStatus =
		changes.bid_sale_auction_status && changes.bid_sale_auction_status !== BID_SALE_AUCTION_STATUS.overtime;
	const extensionTimeWasAdded =
		changes?.bid_sale_auction_extended_until &&
		currentOfferState?.bid_sale_auction_extended_until &&
		dayjs(changes?.bid_sale_auction_extended_until).isAfter(
			dayjs(currentOfferState.bid_sale_auction_extended_until),
		);
	// set isOvertimeCompletedButServerDidNotResolveTheSell to false if a new offer is received on overtime and it extends auction_extended_until field
	if (notOvertimeStatus || extensionTimeWasAdded) {
		changes.isOvertimeCompletedButServerDidNotResolveTheSell = false;
	}

	return changes;
};

export const setOvertimeCompleted = ({ currentOfferState }) => {
	// isOvertimeCompletedButServerDidNotResolveTheSell is a local field (doesn't come from BE) only to keep track
	const vehicleToUpdate = {
		...currentOfferState,
		isOvertimeCompletedButServerDidNotResolveTheSell: true,
	};

	return vehicleToUpdate;
};
